
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __appWithI18n from 'next-translate/appWithI18n'
// @ts-ignore
    
import '@/assets/fonts/iconfont/iconfont.css';
import '@/assets/styles/global.scss';
import '@/assets/styles/reset.css';
import 'intl-pluralrules';

// import '@/assets/fonts/poppins/poppins.css';
import useTranslation from 'next-translate/useTranslation';
import Head from 'next/head';
import Router, { useRouter } from 'next/router';
import Script from 'next/script';
import React from 'react';

import ActivityWidget from '@/components/activity/components/center/ActivityWidget';
import GlobalComponent from '@/components/common/GlobalComponent';
import GlobalLogicComponent from '@/components/common/GlobalLogicComponent';
import UserPreOrder from '@/components/global/UserPreOrder';
import { CDN_PREFETCH_HOST } from '@/constants/cdn';
import Constants from '@/constants/const';
import { FORBIDDEN_JS_PATH, showCommonFacebookOgData } from '@/constants/third';
import KeepAliveProvider from '@/hooks/keepalive/provider';
import useScript, { loadScript } from '@/hooks/useScript';
import { AccountProvider } from '@/store/account';
import NavigationService from '@/store/navigation';
import { ShoppingProvider } from '@/store/shopping/index';
import { ThemeProvider } from '@/store/theme';
import { loadScriptWhenIdle } from '@/utils/script';
import { buildUrlWithOrigin } from '@/utils/url';
import { initWeeeTracker } from '@/utils/weeeTracker';
import { CdnConfigContextProvider } from '@weee-fe/module';
import { GlobalNavHeaderProvider } from '@/hooks/globalnavheader/provider';
import { PROD_GTM, TEST_GTM } from '@/utils/common/gtm';
import { isLatinoChannel } from '@/utils/common/latino-app';
import {
  getCurrentDatagrailConsentLoaderSrc,
  getDatagrailShadowStylesInjectorInlineScript,
  getDatagrailStylesheetHref,
  getOneTrustToDatagrailBridgeInlineScript
} from '@/utils/common/datagrail-consent';
import { logWebVitals } from '@/utils/log-to-server';
import { RAPTOR_EXCEPTION_SCRIPT_SRC, RAPTOR_GROUP_EXPERIMENT, resolveRaptorAbTest } from '@/utils/raptor-ab-test';
import { getCookie } from '@/utils/cookie';
import { PersonalizedCouponProvider } from '@/store/personalized-coupon';

const DATAGRAIL_SHADOW_STYLE_DONE_EVENT = 'weee:datagrail-shadow-style-injector:done';
const DATAGRAIL_SHADOW_STYLE_DONE_FLAG = '__weeeDatagrailShadowStyleInjectorDone__' as const;

const supportLang = isLatinoChannel() ? Constants.LATINO_LANUAGES : Constants.ALL_LANGUAGES;
const alternateLanguages = supportLang.map(item => item.key);
const { LANGUAGE_ZH, LANGUAGE_ZHT, LANGUAGE_ZH_ALIAS, LANGUAGE_ZHT_ALIAS } = Constants;

const getHrefLang = (key: string) => {
  let hrefLang = key;
  switch (key) {
    case LANGUAGE_ZH:
      hrefLang = LANGUAGE_ZH_ALIAS;
      break;
    case LANGUAGE_ZHT:
      hrefLang = LANGUAGE_ZHT_ALIAS;
      break;
    default:
      break;
  }
  return hrefLang;
};

// 动态导入并启用插件
// import('@/utils/product-sorter-plugin')
import('@weee-fe/product-sorter-plugin')
  .then(({ enableProductSorterPlugin }) => enableProductSorterPlugin())
  .catch(error => console.error('Failed to load ProductSorterPlugin:', error));

function MyApp({ Component, pageProps }) {
  const router = useRouter();
  const { query, asPath } = router;
  const { t, lang } = useTranslation('seo');
  const Global = (Component as any).GlobalComponent || GlobalComponent;
  let {
    seoInfo,
    canonicalLink,
    path,
    showSearchAction,
    isPreheat,
    hideGlobalLogic = false,
    httpReqAwareProps
  } = pageProps;
  canonicalLink = canonicalLink || path;
  const preheatPage = query.preheat || isPreheat;

  const streamlinedHeader = httpReqAwareProps?.streamlinedHeader || false;

  if (!seoInfo) {
    seoInfo = {
      title: t('seo_title'),
      description: t('seo_description'),
      h1: t('seo_h1'),
      keywords: t('seo_keywords')
    };
  }

  const isForbidden = FORBIDDEN_JS_PATH.some(path => {
    return asPath.startsWith(path);
  });

  const isForbiddenFb = showCommonFacebookOgData(asPath);

  const env = process.env.ENV_VAR || process.env.NEXT_PUBLIC_ENV_VAR;
  const isTestEnv = env === 'test';
  const isProdEnv = env === 'prod';
  const showCookieConsent = httpReqAwareProps?.showCookieConsent || false;
  const isDatagrailConsentEnabled = showCookieConsent;
  const datagrailConsentLoaderSrc = isDatagrailConsentEnabled ? getCurrentDatagrailConsentLoaderSrc() : '';
  const datagrailStylesheetHref = isDatagrailConsentEnabled ? getDatagrailStylesheetHref(isLatinoChannel()) : '';
  const datagrailShadowInjectorHtml = isDatagrailConsentEnabled
    ? getDatagrailShadowStylesInjectorInlineScript(datagrailStylesheetHref)
    : '';
  const oneTrustToDatagrailBridgeHtml = isDatagrailConsentEnabled ? getOneTrustToDatagrailBridgeInlineScript() : '';
  const [shouldRenderConsentLoader, setShouldRenderConsentLoader] = React.useState(false);
  const renderBlockingDatagrailShadowInjectorHtml = React.useMemo(
    () =>
      `(function(){var doneEvent=${JSON.stringify(DATAGRAIL_SHADOW_STYLE_DONE_EVENT)};var doneFlag=${JSON.stringify(
        DATAGRAIL_SHADOW_STYLE_DONE_FLAG
      )};try{${datagrailShadowInjectorHtml}}catch(e){console.error('[DataGrail] Shadow style injector failed:',e);}finally{window[doneFlag]=true;window.dispatchEvent(new Event(doneEvent));}})();`,
    [datagrailShadowInjectorHtml]
  );
  const currentGtmId = isTestEnv ? TEST_GTM : PROD_GTM;
  const currentGtmSrc = `https://www.googletagmanager.com/gtm.js?id=${currentGtmId}`;

  const cdnHost = process.env.CDN_HOST || process.env.NEXT_PUBLIC_CDN_HOST;

  const normalizePathname = (value?: string | null) => {
    if (!value) {
      return '/';
    }

    return value === '/' ? value : value.replace(/\/$/, '');
  };

  const getCurrentPathname = () => {
    if (typeof window === 'undefined') {
      return normalizePathname(router.pathname);
    }

    return normalizePathname(window.location.pathname || router.pathname);
  };

  const loadGoogleTagManager = () => {
    if (isForbidden || typeof window === 'undefined' || document.getElementById('gtm-script')) {
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });

    const firstScript = document.getElementsByTagName('script')[0];
    const gtmScript = document.createElement('script');
    const parentNode = firstScript?.parentNode || document.head;

    gtmScript.id = 'gtm-script';
    gtmScript.async = true;
    gtmScript.src = currentGtmSrc;
    parentNode.insertBefore(gtmScript, firstScript || null);
  };

  /**
   * Loads the Raptor script and initializes it with the provided configuration.
   */
  const handleFirstScriptLoad = () => {
    const experiment = resolveRaptorAbTest({
      userId: getCookie(Constants.USER_ID),
      sessionId: getCookie(Constants.WEEE_SESSION_TOKEN)
    });
    const isExperimentGroup = experiment.group === RAPTOR_GROUP_EXPERIMENT;
    const initialPath = isExperimentGroup ? getCurrentPathname() : Router.pathname;

    loadScript(experiment.scriptSrc, 'raptor-script', () => {
      if (!(window as any).Raptor) return;
      const experimentContext = isExperimentGroup
        ? {
            raptorAbGroup: experiment.group
          }
        : {};

      const reportParams: {
        projectId: string;
        env: string | undefined;
        path: string;
        devMode: boolean;
        sampleRate: number;
        ignoreErrors: (string | RegExp)[];
        context: Record<string, string>;
      } = {
        projectId: `ec-mg-mweb`,
        env: process.env.NEXT_PUBLIC_SENTRY_ENV,
        path: initialPath,
        devMode: false,
        sampleRate: 0.6,
        ignoreErrors: [
          'https://bat.bing.com',
          'https://connect.facebook.net',
          'www.googletagmanager.com',
          'google-analytics',
          'google',
          'facebook',
          'loggly.com',
          new RegExp(/stats.g.doubleclick.net/)
        ],
        context: {
          // Set the UI type based on the streamlinedHeader prop
          ui: streamlinedHeader ? 'navbar' : 'default',
          ...experimentContext
        }
      };

      const raptor = new (window as any).Raptor(reportParams);
      (window as any).raptor = raptor;
      (window as any).__raptorAbGroup = experiment.group;
      (window as any).__raptorPathAttributionEnabled = experiment.enablePathAttribution;
    });
  };

  React.useEffect(() => {
    if (!isDatagrailConsentEnabled) {
      setShouldRenderConsentLoader(false);
      return;
    }

    const win = window as Window & { [DATAGRAIL_SHADOW_STYLE_DONE_FLAG]?: boolean };
    if (win[DATAGRAIL_SHADOW_STYLE_DONE_FLAG]) {
      setShouldRenderConsentLoader(true);
      return;
    }

    const handleShadowStyleDone = () => {
      setShouldRenderConsentLoader(true);
    };

    window.addEventListener(DATAGRAIL_SHADOW_STYLE_DONE_EVENT, handleShadowStyleDone);
    return () => {
      window.removeEventListener(DATAGRAIL_SHADOW_STYLE_DONE_EVENT, handleShadowStyleDone);
    };
  }, [isDatagrailConsentEnabled]);

  React.useEffect(() => {
    const handleRaptorPathChange = (url: string) => {
      if (!(window as any).__raptorPathAttributionEnabled) {
        return;
      }

      const raptor = (window as any).raptor;
      if (!raptor?.updatePath) {
        return;
      }

      const pathnameOnly = normalizePathname(url.split('?')[0]?.split('#')[0]);
      raptor.updatePath(pathnameOnly);
    };

    Router.events.on('routeChangeComplete', handleRaptorPathChange);

    return () => {
      Router.events.off('routeChangeComplete', handleRaptorPathChange);
    };
  }, []);

  const debugFlag = query?.debug_weee === '1' || isTestEnv;
  useScript('//cdnjs.cloudflare.com/ajax/libs/eruda/2.4.1/eruda.min.js', debugFlag, () => {
    (window as any).eruda.init();
  });

  React.useEffect(() => {
    loadScriptWhenIdle(`${process.env.NEXT_PUBLIC_TRACK_SDK_HOST}/0.0.74/index.js`, 'weee_tracker', {
      onLoad: () => {
        if (!preheatPage) {
          initWeeeTracker();
        }
      },
      maxDelay: 5000,
      retryCount: 1
    });
  }, []);

  return (
    <>
      <Head>
        <meta charSet="utf-8" />
        {isDatagrailConsentEnabled && (
          <>
            <link rel="preconnect" href="https://api.consentjs.datagrail.io" crossOrigin="" />
            <link rel="preconnect" href="https://click.sayweee.com" crossOrigin="" />
            <link rel="preload" href={datagrailStylesheetHref} as="style" />
            <link rel="preload" href={datagrailConsentLoaderSrc} as="script" />
            {!isForbidden && <link rel="preload" href={currentGtmSrc} as="script" />}
            <script
              id="datagrail-shadow-style-injector"
              type="text/javascript"
              dangerouslySetInnerHTML={{ __html: renderBlockingDatagrailShadowInjectorHtml }}
            />
            <script
              id="onetrust-to-datagrail-bridge"
              type="text/javascript"
              dangerouslySetInnerHTML={{ __html: oneTrustToDatagrailBridgeHtml }}
            />
          </>
        )}
        <meta httpEquiv="X-UA-Compatible" content="IE=Edge" />
        {!isProdEnv && (
          <>
            <meta name="robots" content="noindex" />
            <meta name="googlebot" content="noindex" />
          </>
        )}
        {!isForbidden && <link rel="dns-prefetch" href="https://www.googletagmanager.com" />}
        {!isForbidden && <link rel="preconnect" href="https://www.googletagmanager.com" />}
        {!isForbidden && <link rel="dns-prefetch" href="https://www.google-analytics.com" />}
        {!isForbidden && <link rel="preconnect" href="https://www.google-analytics.com" />}
        <link rel="dns-prefetch" href="https://connect.facebook.net" />
        <link rel="preconnect" href="https://connect.facebook.net" />
        {CDN_PREFETCH_HOST.map((item, index) => {
          return (
            <React.Fragment key={index}>
              <link rel="dns-prefetch" href={`https://${item}`} />
              <link rel="preconnect" href={`https://${item}`} />
            </React.Fragment>
          );
        })}
        <link rel="dns-prefetch" href={cdnHost.replace(/\/(\w+)\/(\w+)$/, '')} />
        <link rel="preconnect" href={cdnHost.replace(/\/(\w+)\/(\w+)$/, '')} />
        <link rel="dns-prefetch" href="https://img08.weeecdn.net" />
        <link rel="preconnect" href="https://img08.weeecdn.net" />
        <link rel="dns-prefetch" href={process.env.API_HOST || process.env.NEXT_PUBLIC_API_HOST} />
        <link rel="shortcut icon" href={`/favicons/favicon.ico?v=1`} />
        <link rel="apple-touch-icon" sizes="180x180" href={`/favicons/apple-touch-icon.png`} />
        <link rel="icon" type="image/png" sizes="32x32" href={`/favicons/favicon-32x32.png`} />
        <link rel="icon" type="image/png" sizes="16x16" href={`/favicons/favicon-16x16.png`} />
        <link rel="manifest" href={`/favicons/site.webmanifest`} />
        <link rel="mask-icon" href={`/favicons/safari-pinned-tab.svg`} color="#009ed0" />

        {isForbiddenFb && (
          <React.Fragment>
            <meta
              property="fb:app_id"
              content={`${process.env.WEEE_FACEBOOK_APPID || process.env.NEXT_PUBLIC_WEEE_FACEBOOK_APPID}`}
            />
            <meta property="og:type" content="website" />
            <meta
              property="og:image"
              content={`${
                process.env.CDN_COMMON_ASSETS_PATH || process.env.NEXT_PUBLIC_CDN_COMMON_ASSETS_PATH
              }/public/latino/fb_friendly_1.gif`}></meta>
            <meta property="og:title" content={seoInfo?.title || t('seo_title')}></meta>
            <meta property="og:description" content={seoInfo?.description || t('seo_description')}></meta>
            <meta property="og:url" content={buildUrlWithOrigin(`/${lang}${asPath}`)}></meta>
          </React.Fragment>
        )}

        <meta name="msapplication-TileColor" content="#009ed0"></meta>
        {!streamlinedHeader && <meta name="theme-color" content="#ffffff"></meta>}
        <meta
          key="viewport"
          name="viewport"
          content="width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=2.0, user-scalable=no, viewport-fit=cover"
        />
        {seoInfo?.title ? <title>{seoInfo?.title}</title> : <title>{t('seo_title')}</title>}
        {['description'].map(key => {
          if (!seoInfo?.[key]) {
            return null;
          }
          return <meta key={key} name={key} content={seoInfo?.[key]} />;
        })}
        {canonicalLink && <link rel="canonical" key="canonical" href={buildUrlWithOrigin(canonicalLink)} />}
        {canonicalLink &&
          alternateLanguages.map(key => {
            const hrefLang = getHrefLang(key);
            return (
              <link
                key={key}
                rel="alternate"
                hrefLang={hrefLang}
                href={buildUrlWithOrigin((canonicalLink || '').replace(Constants.LANGUAGE_REGEXP, `/${key}`))}
              />
            );
          })}
        {canonicalLink && (
          <link
            rel="alternate"
            hrefLang="x-default"
            href={buildUrlWithOrigin((canonicalLink || '').replace(Constants.LANGUAGE_REGEXP, '/en'))}
          />
        )}
        {showSearchAction && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `
            {
              "@context": "https://schema.org",
              "@type": "WebSite",
              "url": "https://www.sayweee.com",
              "potentialAction": {
                "@type": "SearchAction",
                "target": {
                  "@type": "EntryPoint",
                  "urlTemplate": "https://www.sayweee.com/${lang}/search?keyword={search_term_string}"
                },
                "query-input": "required name=search_term_string"
              }
            }`
            }}
          />
        )}
        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.ENV= window.ENV || {};ENV.userID = '';
              window.purchaseData = window.purchaseData || {};
              `
          }}
        />
      </Head>

      <Script
        src={RAPTOR_EXCEPTION_SCRIPT_SRC}
        crossOrigin={'anonymous'}
        strategy="lazyOnload"
        onLoad={handleFirstScriptLoad}></Script>

      {isDatagrailConsentEnabled && shouldRenderConsentLoader && (
        <Script
          id="datagrail-consent-loader"
          strategy="afterInteractive"
          type="text/javascript"
          src={datagrailConsentLoaderSrc}
          onLoad={loadGoogleTagManager}
        />
      )}

      {!isForbidden && !isDatagrailConsentEnabled && (
        <Script
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: `
                  (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                  'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                  })(window,document,'script','dataLayer','${currentGtmId}');
              `
          }}
        />
      )}
      {!isForbidden && (
        <>
          <script
            dangerouslySetInnerHTML={{
              __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
          `
            }}
          />
        </>
      )}

      <Script
        strategy="lazyOnload"
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `document.addEventListener("DOMContentLoaded", function() {
                  document.documentElement.addEventListener('touchstart', function (event) {
                    if (event.touches.length > 1) {
                      event.preventDefault();
                    }
                  }, false);
                  var lastTouchEnd = 0;
                  document.documentElement.addEventListener('touchend', function (event) {
                    var now = Date.now();
                    if (now - lastTouchEnd <= 300) {
                      event.preventDefault();
                    }
                    lastTouchEnd = now;
                  }, false);
              });`
        }}
      />

      {/* 性能监控 上传异常指标 */}
      <Script
        src="https://unpkg.com/web-vitals@5/dist/web-vitals.iife.js"
        strategy="lazyOnload"
        onLoad={() => {
          const { webVitals } = window as any;

          if (!webVitals) {
            console.warn('web-vitals library not loaded');
            return;
          }
          //只拦截 /product/ 的页面
          if (!window.location.pathname.includes('/product/')) {
            return;
          }

          // CLS - 布局偏移
          webVitals.onCLS(
            (info: any) => {
              if (info.rating === 'poor') {
                logWebVitals(
                  {
                    type: 'cls',
                    value: info.value.toFixed(3),
                    rating: info.rating,
                    userId: getCookie(Constants.USER_ID)
                  },
                  ['web-vitals', 'cls']
                );
              }
            },
            { reportAllChanges: true }
          );

          // LCP - 最大内容绘制
          webVitals.onLCP((info: any) => {
            if (info.rating === 'poor') {
              logWebVitals(
                {
                  type: 'lcp',
                  value: info.value.toFixed(3),
                  rating: info.rating,
                  userId: getCookie(Constants.USER_ID),
                  domClassName: info?.entries?.[0]?.element?.className,
                  domType: info?.entries?.[0]?.element?.tagName,
                  domSrc: info?.url || info?.entries?.[0]?.element?.src
                },
                ['mweb-vitals', 'mweb-lcp']
              );
            }
          });

          // TTFB - 首字节时间
          webVitals.onTTFB((info: any) => {
            if (info.rating === 'poor') {
              // 获取导航时间信息
              const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
              const paintEntries = performance.getEntriesByType('paint');

              // 计算详细的时间分解
              const timingBreakdown = navigation
                ? {
                    // DNS相关
                    dnsLookup: Math.round(navigation.domainLookupEnd - navigation.domainLookupStart),

                    // 连接相关
                    tcpConnect: Math.round(navigation.connectEnd - navigation.connectStart),
                    sslHandshake:
                      navigation.secureConnectionStart > 0
                        ? Math.round(navigation.connectEnd - navigation.secureConnectionStart)
                        : 0,

                    // 请求响应相关
                    requestTime: Math.round(navigation.responseStart - navigation.requestStart),
                    responseTime: Math.round(navigation.responseEnd - navigation.responseStart),

                    // 重定向
                    redirectTime: Math.round(navigation.redirectEnd - navigation.redirectStart),

                    // 总体时间
                    totalLoadTime: Math.round(navigation.loadEventEnd - navigation.fetchStart),
                    domContentLoaded: Math.round(navigation.domContentLoadedEventEnd - navigation.fetchStart),

                    // 渲染相关
                    firstPaint: paintEntries.find(entry => entry.name === 'first-paint')?.startTime || 0,
                    firstContentfulPaint:
                      paintEntries.find(entry => entry.name === 'first-contentful-paint')?.startTime || 0
                  }
                : null;

              // 获取资源加载信息
              const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
              const slowResources = resources
                .filter(resource => resource.duration > 1000) // 超过1秒的资源
                .sort((a, b) => b.duration - a.duration)
                .slice(0, 5) // 取前5个最慢的资源
                .map(resource => ({
                  name: resource.name.split('/').pop() || resource.name,
                  url: resource.name,
                  duration: Math.round(resource.duration),
                  size: resource.transferSize || 0,
                  type: resource.initiatorType,
                  cached: resource.transferSize === 0 && resource.decodedBodySize > 0
                }));

              // 网络信息（如果支持）
              const connection =
                (navigator as any).connection ||
                (navigator as any).mozConnection ||
                (navigator as any).webkitConnection;
              const networkInfo = connection
                ? {
                    effectiveType: connection.effectiveType,
                    downlink: connection.downlink,
                    rtt: connection.rtt,
                    saveData: connection.saveData
                  }
                : null;

              logWebVitals(
                {
                  type: 'ttfb',
                  value: info.value.toFixed(3),
                  rating: info.rating,
                  userId: getCookie(Constants.USER_ID),

                  // 详细时间分析
                  timingBreakdown,

                  // 慢资源分析
                  slowResources,

                  // 网络信息
                  networkInfo,

                  // 页面基础信息
                  pageInfo: {
                    url: window.location.href,
                    referrer: document.referrer
                  },

                  // 服务器相关（如果可获取）
                  serverInfo: navigation
                    ? {
                        redirectCount: navigation.redirectCount || 0,
                        transferSize: navigation.transferSize || 0,
                        encodedBodySize: navigation.encodedBodySize || 0,
                        decodedBodySize: navigation.decodedBodySize || 0,
                        type: navigation.type
                      }
                    : null

                  // 生成诊断摘要
                  // diagnosis: generateTTFBDiagnosis(
                  //   info.value,
                  //   timingBreakdown,
                  //   slowResources,
                  //   networkInfo
                  // )
                },
                ['mweb-vitals', 'ttfb', 'mweb-load-time']
              );
            }
          });
        }}
      />
      <div className={`${pageProps?.containerClassName ? pageProps?.containerClassName : 'container'}`}>
        {isPreheat || hideGlobalLogic ? (
          <Component {...pageProps} />
        ) : (
          <AccountProvider>
            <NavigationService>
              <ShoppingProvider>
                <UserPreOrder />
                <Global>
                  <GlobalLogicComponent />
                  <GlobalNavHeaderProvider {...pageProps}>
                    <CdnConfigContextProvider cdnSwitchMap={pageProps?.cdnSwitchMap}>
                      <ThemeProvider initialProps={{ ...(pageProps?.themeInfo || {}) }}>
                        <PersonalizedCouponProvider>
                          {/* * KeepAliveProvider必须在component上方，这个provide可能使用了cloneElement的api  */}
                          <KeepAliveProvider>
                            <Component {...pageProps} />
                          </KeepAliveProvider>
                          <ActivityWidget />
                        </PersonalizedCouponProvider>
                      </ThemeProvider>
                    </CdnConfigContextProvider>
                  </GlobalNavHeaderProvider>
                </Global>
              </ShoppingProvider>
            </NavigationService>
          </AccountProvider>
        )}
      </div>
    </>
  );
}

const __Page_Next_Translate__ = MyApp;


// @ts-ignore
    export default __appWithI18n(__Page_Next_Translate__, {
// @ts-ignore
      ...__i18nConfig,
// @ts-ignore
      isLoader: true,
// @ts-ignore
      skipInitialProps: true,
// @ts-ignore
      
// @ts-ignore
    });
// @ts-ignore
  